// Typed client + React Query hooks for the Forms flow.
// Mirrors notifications.ts — local fetch helper, keys colocated, optimistic
// updates on field PATCH so the reviewer sees their edit stick instantly.

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

export type FormPackageStatus =
  | 'draft'
  | 'needs_review'
  | 'ready'
  | 'packaged'
  | 'delivered';

export type FormDocumentStatus =
  | 'pending'
  | 'filling'
  | 'needs_review'
  | 'ready'
  | 'failed';

export type FormFieldSource = 'rule' | 'ai' | 'human';

export interface FormPackageRow {
  id: string;
  rlp_opportunity_id: string;
  candidate_property_id: string;
  offeror_profile_id: string;
  status: FormPackageStatus;
  packaged_r2_key: string | null;
  packaged_at: number | null;
  dedup_key: string;
  created_at: number;
  updated_at: number;
  solicitation_number: string | null;
  agency: string | null;
  legal_entity: string;
  candidate_address: string;
  fields_total: number;
  fields_needs_review: number;
  ai_confidence: number | null;
}

export interface FormFieldRow {
  id: string;
  form_document_id: string;
  field_key: string;
  proposed_value: string | null;
  final_value: string | null;
  source: FormFieldSource;
  confidence: number | null;
  rationale: string | null;
  reviewed_at: number | null;
  reviewed_by: string | null;
  created_at: number;
  updated_at: number;
}

export interface FormDocumentRow {
  id: string;
  form_package_id: string;
  form_key: string;
  blank_r2_key: string;
  filled_r2_key: string | null;
  fields_total: number;
  fields_needs_review: number;
  ai_confidence: number | null;
  status: FormDocumentStatus;
  created_at: number;
  updated_at: number;
  fields: FormFieldRow[];
}

export interface FormPackageDetail {
  package: FormPackageRow;
  documents: FormDocumentRow[];
}

export interface StartFormFillBody {
  rlp_opportunity_id: string;
  candidate_property_id: string;
  // Optional: server falls back to the latest profile for the RLP if omitted,
  // and returns 409 offeror_profile_required when the RLP has no profile yet.
  offeror_profile_id?: string;
}

export interface BuildResultItem {
  form_document_id: string;
  filled_r2_key?: string;
  bytes?: number;
  fields_written?: number;
  error?: string;
}

async function request<T>(path: string, init?: RequestInit): Promise<T> {
  const res = await fetch(path, {
    ...init,
    credentials: 'include',
    headers: {
      Accept: 'application/json',
      ...(init?.body ? { 'Content-Type': 'application/json' } : {}),
      ...init?.headers,
    },
  });
  const text = await res.text();
  if (!res.ok) throw new Error(`api ${res.status}: ${text || res.statusText}`);
  return text ? (JSON.parse(text) as T) : ({} as T);
}

export interface OfferorProfileSummary {
  id: string;
  legal_entity: string;
  poc_name: string | null;
  poc_email: string | null;
  updated_at: number;
}

export interface CreateOfferorProfileBody {
  legal_entity: string;
  trade_style_name?: string | null;
  ein?: string | null;
  sam_uei?: string | null;
  sam_cage?: string | null;
  state_of_incorporation?: string | null;
  date_org_established?: string | null;
  street_address?: string | null;
  city?: string | null;
  state?: string | null;
  zip_code?: string | null;
  poc_name?: string | null;
  poc_email?: string | null;
  poc_phone?: string | null;
}

export const formsApi = {
  list: (params: { status?: FormPackageStatus; limit?: number } = {}) => {
    const qs = new URLSearchParams();
    if (params.status) qs.set('status', params.status);
    if (params.limit != null) qs.set('limit', String(params.limit));
    return request<{ packages: FormPackageRow[] }>(
      `/api/forms${qs.size ? `?${qs}` : ''}`
    );
  },
  get: (id: string) =>
    request<FormPackageDetail>(`/api/forms/${encodeURIComponent(id)}`),
  start: (body: StartFormFillBody) =>
    request<{ package_id: string; document_id: string; offeror_profile_id: string }>(
      `/api/forms/start`,
      {
        method: 'POST',
        body: JSON.stringify(body),
      }
    ),
  patchField: (
    packageId: string,
    fieldId: string,
    body: { final_value: string | null }
  ) =>
    request<{ ok: true; id: string }>(
      `/api/forms/${encodeURIComponent(packageId)}/fields/${encodeURIComponent(fieldId)}`,
      { method: 'PATCH', body: JSON.stringify(body) }
    ),
  build: (id: string) =>
    request<{ ok: boolean; results: BuildResultItem[] }>(
      `/api/forms/${encodeURIComponent(id)}/build`,
      { method: 'POST' }
    ),
  downloadUrl: (id: string) => `/api/forms/${encodeURIComponent(id)}/download`,
  listProfilesForRlp: (rlpId: string) =>
    request<{ profiles: OfferorProfileSummary[] }>(
      `/api/rlps/${encodeURIComponent(rlpId)}/profiles`
    ),
  createProfile: (rlpId: string, body: CreateOfferorProfileBody) =>
    request<OfferorProfileSummary>(
      `/api/rlps/${encodeURIComponent(rlpId)}/profiles`,
      { method: 'POST', body: JSON.stringify(body) }
    ),
};

export const formsKeys = {
  all: ['forms'] as const,
  list: (params: { status?: FormPackageStatus; limit?: number }) =>
    [...formsKeys.all, 'list', params] as const,
  detail: (id: string) => [...formsKeys.all, 'detail', id] as const,
  profilesForRlp: (rlpId: string) =>
    [...formsKeys.all, 'profiles-for-rlp', rlpId] as const,
};

export function useOfferorProfiles(rlpId: string | undefined) {
  return useQuery({
    queryKey: formsKeys.profilesForRlp(rlpId ?? ''),
    queryFn: () => formsApi.listProfilesForRlp(rlpId as string),
    enabled: !!rlpId,
    staleTime: 30_000,
  });
}

export function useCreateOfferorProfile(rlpId: string | undefined) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: CreateOfferorProfileBody) => {
      if (!rlpId) throw new Error('rlpId required');
      return formsApi.createProfile(rlpId, body);
    },
    onSettled: () => {
      if (rlpId) {
        qc.invalidateQueries({ queryKey: formsKeys.profilesForRlp(rlpId) });
      }
    },
  });
}

export function useFormPackages(
  params: { status?: FormPackageStatus; limit?: number } = {}
) {
  return useQuery({
    queryKey: formsKeys.list(params),
    queryFn: () => formsApi.list(params),
    staleTime: 10_000,
  });
}

export function useFormPackage(id: string | undefined) {
  return useQuery({
    queryKey: formsKeys.detail(id ?? ''),
    queryFn: () => formsApi.get(id as string),
    enabled: !!id,
    staleTime: 5_000,
  });
}

export function usePatchFormField(packageId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: { fieldId: string; final_value: string | null }) =>
      formsApi.patchField(packageId, input.fieldId, {
        final_value: input.final_value,
      }),
    // Optimistic: the reviewer taps Save and sees the chip flip to "human"
    // immediately, even before D1 round-trips.
    onMutate: async (input) => {
      await qc.cancelQueries({ queryKey: formsKeys.detail(packageId) });
      const prev = qc.getQueryData<FormPackageDetail>(formsKeys.detail(packageId));
      if (prev) {
        const now = Date.now();
        const next: FormPackageDetail = {
          ...prev,
          documents: prev.documents.map((d) => ({
            ...d,
            fields: d.fields.map((f) =>
              f.id === input.fieldId
                ? {
                    ...f,
                    final_value: input.final_value,
                    source: input.final_value == null ? f.source : 'human',
                    reviewed_at: input.final_value == null ? null : now,
                  }
                : f
            ),
          })),
        };
        qc.setQueryData(formsKeys.detail(packageId), next);
      }
      return { prev };
    },
    onError: (_err, _input, ctx) => {
      if (ctx?.prev) qc.setQueryData(formsKeys.detail(packageId), ctx.prev);
    },
    onSettled: () => {
      qc.invalidateQueries({ queryKey: formsKeys.detail(packageId) });
      qc.invalidateQueries({ queryKey: formsKeys.all });
    },
  });
}

export function useStartFormFill() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: StartFormFillBody) => formsApi.start(body),
    onSettled: () => {
      qc.invalidateQueries({ queryKey: formsKeys.all });
    },
  });
}

export function useBuildFormPackage(id: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () => formsApi.build(id),
    onSettled: () => {
      qc.invalidateQueries({ queryKey: formsKeys.detail(id) });
      qc.invalidateQueries({ queryKey: formsKeys.all });
    },
  });
}

// A field "needs review" when a human hasn't looked at it AND either it
// came from AI below 0.85 confidence OR the package was queued with no
// proposal at all. This mirrors the Worker's CONFIDENCE_REVIEW_THRESHOLD.
export const CONFIDENCE_REVIEW_THRESHOLD = 0.85;

export function fieldNeedsReview(f: FormFieldRow): boolean {
  if (f.reviewed_at != null) return false;
  if (f.final_value != null) return false;
  if (f.proposed_value == null || f.proposed_value === '') return true;
  if (f.source === 'ai' && (f.confidence ?? 0) < CONFIDENCE_REVIEW_THRESHOLD) {
    return true;
  }
  return false;
}

export function displayValue(f: FormFieldRow): string | null {
  return f.final_value ?? f.proposed_value;
}

export function sourceLabel(s: FormFieldSource): string {
  switch (s) {
    case 'human':
      return 'Human';
    case 'ai':
      return 'AI';
    case 'rule':
      return 'Rule';
  }
}

export function statusLabel(s: FormPackageStatus): string {
  switch (s) {
    case 'draft':
      return 'Draft';
    case 'needs_review':
      return 'Needs review';
    case 'ready':
      return 'Ready';
    case 'packaged':
      return 'Packaged';
    case 'delivered':
      return 'Delivered';
  }
}
